import React from 'react';

interface SortableHeaderProps {
  field: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  rowSpan?: number;
  resizing: string | null;
  draggedColumn: string | null;
  dragOverColumn: string | null;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc';
  handleDragStart: (e: React.DragEvent, column: string) => void;
  handleDragOver: (e: React.DragEvent, column: string) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent, targetColumn: string) => void;
  handleDragEnd: () => void;
  handleSort?: (field: string) => void;
  handleResizeStart: (e: React.MouseEvent, column: string) => void;
  columnWidths: { [key: string]: number };
  headerClassName?: string;
  tooltip?: string;
  onMouseEnter?: () => void;
  /** When false, no sort indicator is rendered and the label isn't sort-clickable. Defaults to true. */
  sortable?: boolean;
  /** Compact header cell (no vertical padding; height comes from headerClassName). Default false — unchanged. */
  compact?: boolean;
  /** Allow full multi-line labels in narrow fixed-width columns. */
  wrapLabel?: boolean;
  /** The header remains draggable when its visual grip is hidden. */
  showDragHandle?: boolean;
  /** Floor for the column width when the user has not resized it. Default 100px — unchanged. */
  minWidth?: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  field,
  children,
  style,
  rowSpan,
  resizing,
  draggedColumn,
  dragOverColumn,
  sortField = null,
  sortDirection = 'asc',
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  handleSort,
  handleResizeStart,
  columnWidths,
  headerClassName = '',
  tooltip,
  onMouseEnter,
  sortable = true,
  compact = false,
  wrapLabel = false,
  showDragHandle = true,
  minWidth = '100px',
}) => (
  <th
    rowSpan={rowSpan}
    onMouseEnter={onMouseEnter}
    data-column={field}
    draggable={!resizing}
    onDragStart={(e) => handleDragStart(e, field)}
    onDragOver={(e) => handleDragOver(e, field)}
    onDragLeave={handleDragLeave}
    onDrop={(e) => handleDrop(e, field)}
    onDragEnd={handleDragEnd}
    style={{
      ...style,
      width: columnWidths?.[field] ? `${columnWidths[field]}px` : style?.width,
      minWidth: columnWidths?.[field] ? `${columnWidths[field]}px` : minWidth,
      backgroundColor: headerClassName ? undefined : 'var(--color-background)',
      borderLeftColor: dragOverColumn === field ? 'var(--color-secondary)' : undefined
    }}
    className={`relative group border ${compact ? 'px-1.5 py-0' : 'px-1 py-2'} text-center font-semibold transition-all duration-200 ${
      !resizing ? 'cursor-move' : ''
    } ${
      headerClassName
        ? `${headerClassName} ${draggedColumn === field ? "opacity-50" : ""} ${dragOverColumn === field ? "border-l-4" : ""}`
        : `border-gray-300 text-gray-700 hover:opacity-80 ${
            draggedColumn === field ? "opacity-50" : ""
          } ${
            dragOverColumn === field ? "border-l-4" : ""
          }`
    }`}
    title={tooltip || (typeof children === 'string' ? children : undefined)}
  >
    <div className="flex items-center justify-between w-full overflow-hidden text-ellipsis select-none">
      <div className={`flex items-center ${wrapLabel ? 'min-w-0 flex-1 justify-center' : ''}`}>
        {showDragHandle && (
          <span className="mr-2 text-gray-400 cursor-grab active:cursor-grabbing">⋮⋮</span>
        )}
        <span
          className={`${wrapLabel ? 'min-w-0 whitespace-normal leading-tight' : 'overflow-hidden text-ellipsis'}
            block ${sortable ? 'cursor-pointer' : ''}`}
          onClick={sortable ? (e) => {
            e.stopPropagation();
            handleSort?.(field);
          } : undefined}
        >
          {children}
        </span>
      </div>
      {sortable && (
        <span
          className={`ml-2 text-gray-500 cursor-pointer ${wrapLabel ? 'shrink-0' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleSort?.(field);
          }}
        >
          {sortField === field ? (
            sortDirection === 'asc' ? '↑' : '↓'
          ) : (
            '↕'
          )}
        </span>
      )}
    </div>

    {/* Resize Handle */}
    <div
      className="absolute right-0 top-0 w-1 h-full cursor-col-resize bg-transparent transition-colors duration-200 group-hover:opacity-50"
      style={{ backgroundColor: dragOverColumn === field ? 'var(--color-secondary)' : 'transparent' }}
      onMouseDown={(e) => handleResizeStart(e, field)}
      title="Drag to resize column"
    />
  </th>
);

export default SortableHeader;
